import styled, { css } from 'styled-components'
import { Box } from '@/components/atoms/Grid'
import { MediumTinyBold, Body, Tiny2 } from '@/components/atoms/typography'
import { FC } from 'react'
import { convertDeliveryToStockLabel } from '@/core/timeHelper'
import useTranslation from 'next-translate/useTranslation'
import { Delivery, Product, PromoCode } from '@/network/graphql.g'
import useInView from 'react-cool-inview'
import { useMhub } from '@/providers/mhubProvider'
import { ID } from 'graphql-ws'
import { transparentize } from 'polished'

type ProductSizeBoxProps = {
  value: string
  isSelected?: boolean
} & Rest

export const shortenValue = (value: string): string =>
  value.length > 8 &&
  (/months?/i.test(value) || /years? old/i.test(value) || /years?/i.test(value))
    ? value
        .replace(/months?/i, 'M')
        .replace(/years? old/i, 'Y')
        .replace(/years?/i, 'Y')
    : value.toUpperCase()

export const ProductSizeBoxRedesign: FC<ProductSizeBoxProps> = ({
  value,
  ...rest
}) => {
  return (
    <>
      <StyledProductSizeBoxRedesign {...rest}>
        <StyledMediumTinyBold m="8px 0" textAlign="center">
          {value.toUpperCase()}
        </StyledMediumTinyBold>
      </StyledProductSizeBoxRedesign>
    </>
  )
}

export const BlackProductSizeBox: FC<ProductSizeBoxProps> = ({
  value,
  ...rest
}) => (
  <StyledBlackProductSizeBox {...rest}>
    <Body my="4px" mx={12}>
      {value.toUpperCase()}
    </Body>
  </StyledBlackProductSizeBox>
)

type ProductSizeBoxWithDeliveryProps = {
  id: ID
  delivery: Delivery
  available: number
  isStock: boolean
  product: Product
  position: number
  isDefaultSizePickerDelivery: boolean
  promoCodes: PromoCode[]
} & ProductSizeBoxProps

const ProductSizeBoxWithDelivery: FC<ProductSizeBoxWithDeliveryProps> = ({
  id,
  delivery,
  value,
  product,
  position,
  available,
  isStock,
  isDefaultSizePickerDelivery = false,
  promoCodes,
  ...rest
}) => {
  const { t } = useTranslation('common')
  const { pushImpression } = useMhub()

  const { observe } = useInView({
    unobserveOnEnter: true,
    threshold: [0.5],
    onEnter: () => {
      pushImpression(
        {
          ...product,
          variantId: id,
          variant: value,
          isStock,
          available,
          deliveryTime: delivery
        },
        position,
        `list.detail.size_variants.${product.id}-${product?.MHubProperties?.name}`
      )
    }
  })

  const shortenedValue = shortenValue(value)

  const hasPromoPrice = !!product?.prices?.promoCodePrice

  const getPromoCode = (isStock?: boolean) => {
    if (!promoCodes?.length) {
      return null
    }

    if (promoCodes.length === 1) {
      return promoCodes[0]
    }

    const sortedCodes = [...promoCodes].sort(
      (a, b) => (b.discountValue || 0) - (a.discountValue || 0)
    )

    if (isStock) {
      return sortedCodes[0]
    }

    return sortedCodes[sortedCodes.length - 1]
  }

  const currentPromoCode = getPromoCode(isStock)

  const shouldShowDiscount = currentPromoCode?.discountValue && hasPromoPrice

  return (
    <div ref={observe}>
      <>
        <StyledProductSizeWithDeliveryNew {...rest}>
          {shouldShowDiscount ? (
            <StockDiscountWrapper>{`-${currentPromoCode?.discountValue}%`}</StockDiscountWrapper>
          ) : null}
          <StyledBodyNew>{shortenedValue}</StyledBodyNew>
          <DeliveryTimeBoxNew
            isHighlighted={delivery.highest <= 1}
            isDefaultSizePickerDelivery={isDefaultSizePickerDelivery}
          >
            <StyledTiny m={0}>
              {convertDeliveryToStockLabel(delivery, t)}
            </StyledTiny>
          </DeliveryTimeBoxNew>
        </StyledProductSizeWithDeliveryNew>
      </>
    </div>
  )
}

// export default ProductSizeBoxWithDelivery

const BaseSizeBox = css`
  justify-content: center;
  &:hover {
    cursor: pointer;
  }
`

const StyledProductSizeBoxRedesign = styled(Box)`
  ${BaseSizeBox};
  border-radius: 2px;
  color: ${(props) =>
    props.isSelected
      ? props.theme.colors.background
      : props.theme.colors.onBackground};
  background-color: #f3f3f3;
  padding: 0 2px;
`

const StyledMediumTinyBold = styled(MediumTinyBold)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StyledBlackProductSizeBox = styled(Box)`
  ${BaseSizeBox};
  border: 1px solid ${(props) => props.theme.colors.onBackground};
  border-radius: ${(props) => props.theme.buttonBorderRadius}px;
  color: ${(props) =>
    props.isSelected
      ? props.theme.colors.background
      : props.theme.colors.onBackground};
  background-color: ${(props) =>
    props.isSelected ? props.theme.colors.onBackground : 'transparent'};
  &:hover {
    background-color: ${(props) => props.theme.colors.onBackground};
    color: ${(props) => props.theme.colors.background};
  }
`

type DeliveryTimeBoxProps = {
  isHighlighted: boolean
}

const StyledProductSizeWithDeliveryNew = styled(Box)`
  position: relative;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border: ${(props) =>
      `${props.isSelected ? '2px' : '1px'} solid ${
        props.isSelected
          ? props.theme.colors.onBackground
          : transparentize(0.8, props.theme.colors.onBackground)
      }`};
    pointer-events: none;
    border-radius: ${(props) => (props.isSelected ? '2px' : '1px')};
  }
`

const StyledBodyNew = styled(Tiny2)`
  text-align: center;
  margin: 0 auto;
  padding: 8px 0;
  font-size: 12px;
  font-weight: bold;
`

const DeliveryTimeBoxNew = styled<DeliveryTimeBoxProps>(Box)`
  margin-top: -2px;
  padding: 3px 0;
  text-align: center;
  border-bottom-left-radius: ${(props) => (props.isSelected ? '2px' : '1px')};
  border-bottom-right-radius: ${(props) => (props.isSelected ? '2px' : '1px')};
  background-color: ${(props) =>
    props.theme.colors.menuItemBackgroundSecondary};
`

const StyledTiny = styled<DeliveryTimeBoxProps>(Tiny2)`
  font-weight: 400;
  text-align: center;
`

const StockDiscountWrapper = styled(MediumTinyBold)`
  position: absolute;
  top: -16px;
  right: -6px;
  background-color: ${(props) => props.theme.colors.accent};
  color: ${(props) => props.theme.colors.background};
  font-weight: bold;
  padding: 1px 2px;
  border-radius: 3px;
  z-index: 1;
`
